import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SiteModel } from 'src/app/models/site.model';
import { SitesService } from 'src/app/shared/sites.service';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilsService } from 'src/app/shared/utils.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { RBACService } from '../../../../shared/rbac.service';

@Component({
  selector: 'app-sites-eep-view',
  templateUrl: './sites-eep-view.component.html',
  styleUrls: ['./sites-eep-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesEepViewComponent implements OnInit {
  site: SiteModel = new SiteModel();

  parent_id: number;
  child_id: number;
  site_id: number;

  constructor(
    private siteService: SitesService,
    private route: ActivatedRoute,
    private router: Router,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService
  ) {}

  ngOnInit(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_EEP,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      this.router.navigate(['/sites']);
      return;
    }

    // Get the site id from the route params.
    this.parent_id = Number(this.route.parent.snapshot.params['parent_id']);
    this.child_id = Number(this.route.parent.snapshot.params['child_id']);

    // Check if we are updating a site or creating a new one.
    if (
      this.child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        this.parent_id)
    ) {
      // Store the site id.
      this.site_id = this.child_id ? this.child_id : this.parent_id;

      this.site.id = this.site_id;

      this.siteService.find(this.site.id, true).subscribe(
        (response): void => {
          this.site = response.data;
        }
      );
    }
  }

  /**
   * Exports the emergency evacuation plan for the current site by making an API call.
   * The method checks user permissions before proceeding. If the user lacks the
   * necessary permissions, an access denied message is displayed. Upon success,
   * the evacuation plan is opened in a new browser tab.
   *
   * @return {void} This method does not return any value.
   */
  onExportEmergencyEvacuationPlan(): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_EEP,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.api
      .makeRequestObservable(
        'get',
        `v1/sites/${this.site.id}/emergency-evacuation-plan`
      )
      .toPromise()
      .then(
        (response: any): void => {
          window.open(response.data.url, '_blank');
        }
      );
  }

  /**
   * Initiates the download of the emergency evacuation plan map for the specified site.
   *
   * @param {SiteModel} site - The site model object containing details of the site, including the evacuation map URL.
   * @return {void} This method does not return a value.
   */
  onDownloadEmergencyEvacuationPlanMap(site: SiteModel): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_EEP,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.onDownloadFromTemporaryURL(
      site.evacuation_map_url,
      'Emergency Evacuation Plan Map.' +
        site.evacuation_map_url.split('.').pop()
    );
  }

  /**
   * Opens the specified URL using the utility function.
   *
   * @param {string} url - The URL to be opened.
   * @return {void} This method does not return a value.
   */
  onOpenUrl(url: string): void {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_EEP,
            RBACService.RBAC_A.A_VIEW
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    this.utils.openUrl(url);
  }

  /**
   * Notifies the user that the URL has been copied to their clipboard via a toast message.
   *
   * @return {void} Does not return any value.
   */
  onCopiedURL(): void {
    this.utils.showToast('The URL is copied to your clipboard.');
  }

  protected readonly RBACService = RBACService;
}
